import {
  DirectionsCarFilled as AssetsIcon,
  PeopleAlt as CustomersIcon,
  GridViewSharp as DashboardIcon,
  RoomService as ReservationsIcon,
  LibraryBooks as ContractIocn,
  Settings as SettingsIcon,
  LocationOn,
  VerifiedUser,
  PersonSearch,
  Person,
  Commute as CommuteIcon,
  DirectionsCar,
  DriveFileRenameOutlineOutlined as DriveFileRenameOutlineOutlinedIcon,
  GradingOutlined as GradingOutlinedIcon,
  CarCrash,
  ElectricCar,
  AttachMoneyOutlined,
  FolderShared,
  Payment,
  Assessment,
  PersonAdd,
  Article,
  EventNote,
  LocalOffer,
  AccountBalanceOutlined,
} from "@mui/icons-material";
import { Box, Stack, SvgIcon, SvgIconProps, Typography } from "@mui/material";
import React from "react";
import InfoTooltip from "src/shared_components/InfoTooltip";
import { useTranslation } from "react-i18next";

type IconSize = "small" | "medium" | "large";

interface IconProps extends SvgIconProps {
  iconName?: string;
  fontSize: IconSize;
  title?: string;
  isWithIcon?: boolean;
}

const IconComponent: React.FC<IconProps> = ({
  iconName,
  fontSize = "large",
  title = "",
  isWithIcon = true,
  ...props
}) => {
  const { t } = useTranslation();

  const getTooltipKey = (icon?: string, titleStr?: string): string => {
    if (!icon) return "";
    const name = icon.toLowerCase();

    if (name === "addcircle") {
      const titleLower = (titleStr || "").toLowerCase().replace(/\s+/g, " ");
      if (titleLower.includes("tax mapping")) return "taxMapping";
    }

    if (name === "report") {
      const titleLower = (titleStr || "").toLowerCase().replace(/\s+/g, " ");
      if (titleLower.includes("customer list") || titleLower.includes("liste des clients")) return "customerList";
      if (titleLower.includes("vehicle list") || titleLower.includes("liste des véhicules")) return "vehicleList";
      if (titleLower.includes("utilization") || titleLower.includes("utilisation")) return "vehicleUtilizationReport";
      if (titleLower.includes("rental summary") || titleLower.includes("sommaire des locations")) return "rentalSummaryReport";
      if (titleLower.includes("current rentals") || titleLower.includes("locations en cours")) return "currentRentalsReport";
      if (titleLower.includes("completed rentals") || titleLower.includes("locations terminées")) return "completedRentalsReport";
      if (titleLower.includes("pending payment") || titleLower.includes("paiements en attente")) return "pendingPaymentReport";
      if (titleLower.includes("reservation summary") || titleLower.includes("sommaire des réservations")) return "reservationSummaryReport";
      if (titleLower.includes("daily summary") || titleLower.includes("sommaire quotidien")) return "dailySummaryReport";
      if (titleLower.includes("daily settlement") || titleLower.includes("règlement quotidien")) return "dailySettlementReport";
      if (titleLower.includes("referral") || titleLower.includes("recommandation")) return "referralReport";
      if (titleLower.includes("revenue by fleet") || titleLower.includes("revenus par flotte")) return "revenueByFleet";
      if (titleLower.includes("financial summary") || titleLower.includes("sommaire financier")) return "financialSummaryReport";
      if (titleLower.includes("financial") || titleLower.includes("financier")) return "financialReport";
      if (titleLower.includes("earning breakdown") || titleLower.includes("répartition des gains")) return "earningBreakdownReport";
      if (titleLower.includes("user log") || titleLower.includes("journal des utilisateurs")) return "userLogReport";
    }

    // Settings pages with module="vehicleMake" can have different titles (Make, Model, Type)
    if (name === "vehiclemake" || name === "make") return "makes";
    if (name === "model") return "models";
    if (name === "type") return "types";
    if (name === "option") return "options";

    const mapping: Record<string, string> = {
      dashboard: "dashboard",
      customer: "customers",
      asset: "vehicles",
      reservation: "bookings",
      contract: "contracts",
      invoice: "invoice",
      claim: "insuranceClaims",
      report: "reports",
      planner: "dailyPlanner",
      quickbooks: "quickBooks",
      location: "locations",
      insurance: "insuranceProviders",
      offensetype: "offenseType",
      referral: "referral",
      vendor: "vendor",
      source: "source",
      paymentmethodtype: "paymentMethodType",
      user: "users",
      profile: "profile",
      changepassword: "changePassword",
      role: "roleManagement",
      tax: "tax",
      taxmapping: "taxMapping",
      expense: "expense",
      ratename: "rateNames",
      rateplan: "ratePlans",
      addoncharge: "addOnCharges",
      promotion: "promotions",
      energycost: "energyCost",
      fuelprice: "energyCost",
      client: "clientProfile",
      clientprofile: "clientProfile",
      storeconfiguration: "storeConfiguration",
      termsandconditions: "termsAndConditions",
      termsandcondition: "termsAndConditions",
      fieldconfigurations: "fieldConfigurations",
      fieldconfiguration: "fieldConfigurations",
      maintenanceschedule: "maintenanceSchedule",
      vehiclemaintenanceschedule: "maintenanceSchedule",
      telematics: "telematics",
      currencyrate: "currencyRate",
    };

    return mapping[name] || "";
  };

  const tooltipKey = getTooltipKey(iconName, title);
  const tooltipText = tooltipKey ? t(`tooltipdescription.${tooltipKey}`) : "";
  const hasTooltip = tooltipText && tooltipText !== `tooltipdescription.${tooltipKey}`;

  const getIconComponent = () => {
    switch (iconName) {
      case "dashboard":
        return <DashboardIcon />;
      case "customer":
        return <CustomersIcon />;
      case "asset":
        return <AssetsIcon />;
      case "reservation":
        return <ReservationsIcon />;
      case "contract":
        return <ContractIocn />;
      case "setting":
        return <SettingsIcon />;
      case "location":
        return <LocationOn />;
      case "insurance":
        return <VerifiedUser />;
      case "user":
        return <PersonSearch />;
      case "profile":
        return <Person />;
      case "make":
        return <CommuteIcon />;
      case "model":
        return <DirectionsCar />;
      case "type":
        return <CarCrash />;
      case "option":
        return <ElectricCar />;
      case "rateName":
        return <DriveFileRenameOutlineOutlinedIcon />;
      case "ratePlan":
        return <GradingOutlinedIcon />;
      case "tax":
        return <AttachMoneyOutlined />;
      case "client":
        return <FolderShared />;
      case "payment":
        return <Payment />;
      case "finance":
        return <Assessment />;
      case "additionalDriver":
        return <PersonAdd />;
      case "driver":
        return <PersonAdd />;
      case "report":
        return <Article />;
      case "planner":
        return <EventNote />;
      case "addOnCharge":
        return <Payment />;
      case "promotion":
        return <LocalOffer />;
      case "quickbooks":
        return <AccountBalanceOutlined />;
      default:
        return null;
    }
  };

  return title ? (
    <>
      {isWithIcon ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            bgcolor="primary.contrastText"
            color="primary.main"
            borderRadius="50%"
            p={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <SvgIcon {...props} fontSize={fontSize}>
              {getIconComponent()}
            </SvgIcon>
          </Box>
          <Typography variant="h5" color="primary" sx={{ ml: 1 }}>
            {title}
            {hasTooltip && (
              <InfoTooltip title={tooltipText} sx={{ ml: 1, verticalAlign: "middle" }} />
            )}
          </Typography>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            fontSize={"45px"}
            fontWeight={700}
            color="primary"
            sx={{ lineHeight: "42px" }}
          >
            {title}
            {hasTooltip && (
              <InfoTooltip title={tooltipText} sx={{ ml: 1, verticalAlign: "middle" }} />
            )}
          </Typography>
        </Stack>
      )}
    </>
  ) : (
    <SvgIcon {...props} fontSize={fontSize}>
      {getIconComponent()}
    </SvgIcon>
  );
};

export default React.memo(IconComponent);
